import './ControlledLabeledCheckbox.scss';

import React, { useCallback } from 'react';
import { useController, UseControllerProps } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Toggle } from 'rsuite';
import { Checkbox, Form, Label } from 'semantic-ui-react';

type LabeledCheckboxProps = Omit<React.ComponentProps<typeof Toggle>, 'label'>;

type ControlledLabeledCheckboxProps<ControlledState> = UseControllerProps<ControlledState> &
  LabeledCheckboxProps & {
    label: string | React.ReactNode;
  };

function LabeledCheckbox({
  labelInput,
  ...props
}: LabeledCheckboxProps & {
  labelInput: string | React.ReactNode;
}) {
  return (
    <div className="ui labeled input">
      <Label>{labelInput}</Label>
      <Toggle {...props} />
    </div>
  );
}

export default function ControlledLabeledCheckbox<ControlledState>({
  control,
  name,
  rules,
  defaultValue,
  shouldUnregister,
  disabled,
  label,
  ...inputProps
}: ControlledLabeledCheckboxProps<ControlledState>) {
  const intl = useIntl();
  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
    shouldUnregister,
    disabled,
  });

  const onChecked = useCallback(
    (checked: boolean, e: React.SyntheticEvent<HTMLElement>) => {
      field.onChange(checked);
    },
    [field?.onChange]
  );

  return (
    <Form.Field
      {...inputProps}
      as="label"
      className="controlled-labeled-checkbox"
      error={fieldState.error && { content: fieldState.error.message }}
      control={LabeledCheckbox}
      required={!!rules?.required}
      checked={field.value}
      onBlur={field.onBlur}
      onChange={onChecked}
      name={field.name}
      disabled={field.disabled}
      labelInput={label}
      checkedChildren={intl.formatMessage({ id: 'general.ON' })}
      unCheckedChildren={intl.formatMessage({ id: 'general.OFF' })}
    />
  );
}
